<template>
  <Draggable
    id="planner"
    ref="plannerEl"
    v-hotkey="shortcuts"
    tag="div"
    class="planner"
    :options="draggableOptions"
    @drag:start="start"
    @drag:stop="stop"
    @mirror:created="mirrorCreated"
    @mirror:move="mirrorMove"
    @contextmenu.prevent
  >
    <BaseResizableLeftPane
      v-if="isLeftPaneShown"
      :is-resizable="true"
      :min-width="252"
      :max-width="600"
      :storage-key="`${storagePrefix}:panes/left:width`"
      @resized="onSidebarResize"
    >
      <!--
        A selected event ALWAYS takes over the left pane, even when a caller
        overrides the `left-pane` slot (e.g. a canvas-based route). The
        slot (custom or default) only renders when no event is selected; the
        default content is the standard planner sidebar (task batches / date
        picker).
      -->
      <SidebarEvent
        v-if="openEventId && openEvent"
        :key="openEvent.id"
        :is-draft-event="isOpenEventDraft"
        :is-editable="openEvent.editable"
        :has-changes="hasOpenEventChanges"
        :is-updating="isOpenEventUpdating"
        :props-edited="propsEdited"
        :task="openEventTask || undefined"
        :event="openEvent"
        @confirm-changes="confirmOpenEventChanges"
        @discard-changes="discardOpenEventChanges"
        @kill-event-details="killEventDetails"
        @update-planner-view="updatePlannerView"
        @open-custom-recurrence="openCustomRecurrence"
      />
      <slot v-else name="left-pane">
        <!--
          The task sections (Today / Week / Overdue / …) show in every
          layout. Month view has no sidebar date picker: the header's date
          popover and the month grid itself already cover date navigation.
        -->
        <!--
          Only the Week section follows navigation (see `sidebarWeek`); the
          other sections stay anchored to today, so the period is always
          "current": navigating the calendar never hides the task lists.
        -->
        <SidebarBatch
          ref="sidebarBatchEl"
          :week="sidebarWeek"
          :is-past-week="isSidebarWeekPast"
          period="current"
          :week-task-ids="sidebarWeekTaskIds"
          :overdue-task-ids="overdueTaskIds"
          :upcoming-task-ids="upcomingTaskIds"
          :unplanned-task-ids="unplannedTaskIds"
          :is-loading-tasks="isLoadingTasks"
          :is-dragging-task="isDraggingTask"
          :is-foot-task-list-active="isFootTaskListActive"
          :is-outside-today-drop-zone="isOutsideSidebarTodayDropZone"
          :is-outside-week-drop-zone="isOutsideSidebarWeekDropZone"
          :is-outside-upcoming-drop-zone="isOutsideSidebarUpcomingDropZone"
          :is-outside-unplanned-drop-zone="isOutsideSidebarUnplannedDropZone"
          :local-storage-prefix="storagePrefix"
          @unselect-items="unselectItems"
          @setup-drop-zone="setupDropZone"
        />
      </slot>
    </BaseResizableLeftPane>
    <BaseIntegrationMissing
      v-if="hasCalendar !== 'synchronized'"
      class="no-default-calendar"
      feature="calendar"
      :synchronizing="hasCalendar === 'initializing'"
      empty-state-variant="planner"
    />
    <BaseNoDefaultCalendar
      v-else-if="!defaultCalendar"
      class="no-default-calendar"
    />
    <div
      v-else
      :class="['days-wrapper', { isMonthView: !isWeekView }, { isWeekView }]"
    >
      <Suspense>
        <PlannerCalendar
          :key="currentDay"
          ref="plannerCalendarEl"
          :is-current-period="isCurrentPeriod"
          :default-calendar-id="defaultCalendar?.id"
          :selected-calendar-id="selectedCalendarId"
          :dates="dates"
          :is-dragging-task="isDraggingTask"
          :open-event-id="openEventId"
          :has-open-event="!!openEventId"
          :has-open-event-changes="hasOpenEventChanges"
          :calendar-date="calendarDate"
          :left-panel-width="leftPanelDisplayedWidth"
          :is-three-day-view="isThreeDayView"
          :custom-days="customDays"
          :is-week-view="isWeekView"
          :local-storage-prefix="calendarStoragePrefix"
          :is-left-pane-shown="isLeftPaneShown"
          :is-left-pane-available="isLeftPaneAvailable"
          :show-date-navigation="showDateNavigation"
          :show-layout-selector="showLayoutSelector"
          :selectable-layouts="selectableLayouts"
          :layout-shortcuts="layoutShortcuts"
          :show-zoom-buttons="showZoomButtons"
          :show-timezones="showTimezones"
          :embedded="embedded"
          :read-only="readOnly"
          :allow-context-menu="allowContextMenu ?? !readOnly"
          :is-weekend-shown="isWeekendShown"
          :is-drag-canceled="isDragCanceled"
          :is-dragging-resizing="isDraggingResizing"
          @update-calendar-date="updateCalendarDate"
          @select-layout="selectLayout"
          @update-layout-shortcut="updateLayoutShortcut"
          @is-drag-canceled="(v) => (isDragCanceled = v)"
          @is-dragging-resizing="(v) => (isDraggingResizing = v)"
          @is-dragging-task="setDraggingTask"
          @select-event="selectEvent"
          @kill-event-details="killEventDetails"
          @setup-drop-zone="setupDropZone"
        />
      </Suspense>
      <PlannerFooter
        v-if="isWeekView && showBottomPane"
        ref="footerEl"
        :is-loading="isLoadingFooterTasks"
        :is-dragging-task="isDraggingTask"
        :is-three-day-view="isThreeDayView"
        :custom-days="customDays"
        :week-dates="dates"
        :days="footerDays"
        :is-weekend-shown="isWeekendShown"
        :local-storage-prefix="storagePrefix"
        :is-outside-drop-zones="isOutsideFooterDropZones"
        @unselect-items="unselectItems"
        @setup-drop-zone="setupDropZone"
        @is-foot-task-list-active="(v) => (isFootTaskListActive = v)"
      />
    </div>
  </Draggable>
</template>
<script lang="ts">
import {
  addDays,
  differenceInDays,
  eachDayOfInterval,
  getDay,
  isAfter,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { cloneDeep, debounce, isEqual } from "lodash-es";
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  PropType,
  provide,
  ref,
  watch,
} from "vue";
import { useRoute } from "vue-router";
import { Draggable } from "vue-shopify-draggable";

import controller from "@ctrl/controller";
import type { Week } from "@ctrl/controller-rpc";

import BaseIntegrationMissing from "@/components/base/BaseIntegrationMissing.vue";
import BaseNoDefaultCalendar from "@/components/base/BaseNoDefaultCalendar.vue";
import BaseResizableLeftPane from "@/components/base/BaseResizableLeftPane.vue";
import * as inject from "@/components/inject";
import { useEvent } from "@/components/planner/controllers/event";
import { UseSettings } from "@/components/settings/controller/settings";
import { useCalendarSwitcher } from "@/composables/useCalendarSwitcher";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { getDates } from "@/utils/date/get-dates";
import { pause } from "@/utils/pause";

import {
  assignShortcuts,
  goToNextPeriod,
  goToNow,
  goToPreviousPeriod,
  Shortcuts,
} from "../shortcuts";
import { useDraggable } from "./controllers/draggable";
import {
  DEFAULT_SELECTABLE_LAYOUTS,
  formatCalendarDate,
  keyUseLeftPane,
  layoutToViewState,
  PLANNER_LAYOUTS,
  PlannerLayout,
  PlannerSelectableLayouts,
  useLeftPane,
} from "./controllers/planner";
import { useTasks } from "./controllers/tasks";
import { plannerStoragePrefix, usePlanner } from "./controllers/usePlanner";
import PlannerCalendar from "./PlannerCalendar.vue";
import PlannerFooter from "./PlannerFooter.vue";
import SidebarBatch from "./sidebar/SidebarBatch.vue";
import SidebarEvent from "./sidebar/SidebarEvent.vue";

const { weekOfDate } = controller;

/**
 * Which planner UI elements are displayed (see the `display` prop). Omitted
 * fields default to shown.
 * - `date`: the header date display (with its picker) and the
 *   prev/next/today arrows — hidden by hosts whose own picker drives the
 *   calendar through the `date` prop
 * - `zoom`: the header zoom in/out buttons (week views only)
 * - `layouts`: the header's customizable three-button layout selector
 * - `timezones`: the multi-timezone axis (see PlannerCalendar)
 * - `sidebar`: whether the left task sidebar can be displayed at all
 * - `footer`: the per-day task bottom pane (week views only)
 */
export type PlannerDisplay = {
  date?: boolean;
  zoom?: boolean;
  layouts?: boolean;
  timezones?: boolean;
  sidebar?: boolean;
  footer?: boolean;
};

const isPlannerLayout = (value: string): value is PlannerLayout =>
  PLANNER_LAYOUTS.some((layout) => layout === value);

// TODO(planner): restructure so embeds don't pay for the full planner.
//
// Today this component is both the /calendar route shell and the only viable
// embedding surface (dashboard calendar widget, canvas calendar widget):
// PlannerCalendar is a pure view over FullCalendar whose required props
// (calendarDate + the `dates` window, the isWeekView/isThreeDayView/
// isWeekendShown/customDays view state, isCurrentPeriod, open-event and
// drag state, …) all live here. Embedders therefore mount the whole
// Planner and switch the extras off through props (`display`, `readOnly`,
// `embedded`), but still pay for state they never render: useTasks keeps
// the sidebar/footer task lists loaded, and useEvent/useDraggable are set
// up even when read-only mode can never drag or edit.
//
// The restructuring, when an embed needs to be lighter (e.g. several
// calendar widgets on one canvas):
// 1. Extract the calendar *controller* into a composable (say
//    usePlannerView): calendarDate + the debounced `dates` window
//    computation (week-start anchoring, rolling N-day spans, month range
//    readback), the persisted view-state refs from usePlanner,
//    selectLayout/goToDate/goToday and isCurrentPeriod. That is exactly
//    the state PlannerCalendar's props require.
// 2. Make the heavy concerns opt-in acquisitions of the host instead of
//    unconditional setup here: tasks (useTasks), event details (useEvent)
//    and drag & drop (useDraggable) are only wired when the host renders a
//    sidebar/footer or is editable.
// 3. Lightweight hosts then mount PlannerCalendar (+ CalendarHeader)
//    directly on top of usePlannerView, and this component becomes the
//    full-featured composition: usePlannerView + sidebar + footer + event
//    pane + dnd.
//
// Until then, prefer embedding Planner with `display` flags — duplicating
// the dates-window / view-state logic around a bare PlannerCalendar is the
// one thing this layout is meant to prevent.
export default defineComponent({
  name: "Planner",
  components: {
    BaseIntegrationMissing,
    BaseNoDefaultCalendar,
    BaseResizableLeftPane,
    Draggable,
    PlannerCalendar,
    PlannerFooter,
    SidebarBatch,
    SidebarEvent,
  },
  props: {
    /** Which UI elements are displayed: see `PlannerDisplay`. Omitted
     * fields default to shown. */
    display: {
      type: Object as PropType<PlannerDisplay>,
      default: () => ({}),
    },
    /**
     * Controlled calendar date: when set, the planner displays the period
     * containing this date and follows its changes — the host's own date
     * picker is the source of truth (typically combined with
     * `display.date: false`, since the header navigation would fight the
     * controlling picker).
     */
    date: {
      type: Date,
      default: undefined,
    },
    /**
     * Force a specific layout. When set, the planner uses a local layout state
     * driven by this prop instead of the shared, persisted calendar settings.
     * When omitted, it falls back to the user's saved calendar layout.
     */
    defaultLayout: {
      type: String as PropType<PlannerLayout>,
      default: undefined,
    },
    /**
     * Which layouts the layout-selector dropdowns expose (when
     * `display.layouts` is enabled). Omitted/false keys are hidden.
     * Defaults to every supported layout.
     */
    selectableLayouts: {
      type: Object as PropType<PlannerSelectableLayouts>,
      default: () => ({ ...DEFAULT_SELECTABLE_LAYOUTS }),
    },
    /**
     * Display-only calendar: no event editing (drag/resize), no drag-to-create,
     * no task drops and no context menu. Clicking an event still selects it —
     * without a left pane the selection is a pure highlight, no details are
     * shown — and double-clicking opens it.
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Allow the per-event right-click context menu even on a `readOnly`
     * calendar (defaults to `!readOnly`). The canvas calendar widget uses
     * this: the grid stays display-only — no drag-to-create, no drag/resize —
     * but deliberate actions through the menu are fine.
     */
    allowContextMenu: {
      type: Boolean,
      default: undefined,
    },
    /**
     * Route path this planner reads its stored calendar display preferences
     * from (timezones, zoom level, all-day slot state), instead of its own
     * route. The dashboard's calendar widget passes "/calendar" so it renders
     * with the same preferences as the main calendar. Layout and pane state
     * remain per-instance.
     */
    instanceRoute: {
      type: String,
      default: undefined,
    },
    /**
     * Storage key this planner's own view state (layout, panes) is persisted
     * under, instead of the current route path. Hosts that embed several
     * planners on one route (e.g. canvas calendar widgets) pass a per-widget
     * key so the instances don't share state.
     */
    storageKey: {
      type: String,
      default: undefined,
    },
    /** Inline calendar header instead of the teleported one: see
     * CalendarHeader's `embedded` prop. */
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  async setup(props) {
    const router = inject.router();
    const controller = inject.controller();
    const baseLogger = inject.logger();
    const calendars = inject.calendars();
    const today = inject.today();
    const profile = inject.profile();
    const hasCalendar = inject.hasCalendar();
    const simpleModal = inject.simpleModal();
    const repeatModal = inject.repeatModal();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const multipleEventsModal = inject.multipleEventsModal();
    const route = useRoute();
    const releaser = useReleaser();
    const plannerEl = ref<InstanceType<typeof Draggable>>();
    const plannerCalendarEl = ref<InstanceType<typeof PlannerCalendar>>();
    const sidebarBatchEl = ref<InstanceType<typeof SidebarBatch>>();
    const unselectSidebarTask = () => {
      sidebarBatchEl.value?.unselectTask?.();
    };
    const calendarAPI = () => plannerCalendarEl.value?.calendarAPI?.();
    watch(sidebarBatchEl, () => calendarAPI()?.updateSize());
    const nextPeriod = () => {
      plannerCalendarEl.value?.nextPeriod();
    };
    const prevPeriod = () => {
      plannerCalendarEl.value?.prevPeriod();
    };
    const { isWeekStartOnSunday } = UseSettings.getInstance();
    // Per-route planner state: each Planner instance owns its own refs, with the
    // view-structure flags persisted to local storage (keyed by account + route
    // path). `defaultLayout` only provides the first-visit defaults; the stored
    // value wins afterwards. See `usePlanner`. Because nothing is shared
    // between instances, two planners can no longer collide over a common date.
    const {
      calendarDate,
      isWeekView,
      isThreeDayView,
      isWeekendShown,
      customDays,
      layoutShortcuts,
      storagePrefix,
      release: releasePlannerSettings,
    } = usePlanner(
      props.storageKey ?? route.path,
      profile.value.user.accountId,
      props.defaultLayout
    );
    releaser.defer(releasePlannerSettings);
    // Apply deep-link state before setup starts date-dependent subscriptions
    // such as useTasks. Waiting until onMounted would first load the default
    // week and then load the requested one.
    const applyRouteViewState = () => {
      const layoutQuery = route.query.layout;
      if (typeof layoutQuery === "string" && isPlannerLayout(layoutQuery)) {
        const state = layoutToViewState(layoutQuery);
        isWeekView.value = state.isWeekView;
        isThreeDayView.value = state.isThreeDayView;
        isWeekendShown.value = state.isWeekendShown;
        customDays.value = state.customDays;
      }
      const dateQuery = route.query.date;
      if (typeof dateQuery === "string") {
        const parsed = parseISO(dateQuery);
        if (
          !Number.isNaN(parsed.getTime()) &&
          !isEqual(parsed, calendarDate.value)
        )
          calendarDate.value = parsed;
      }
    };
    applyRouteViewState();
    // A controlled date beats any deep-linked one: seeded before the
    // date-dependent subscriptions below (useTasks), like the route state.
    if (props.date) calendarDate.value = props.date;
    // Display preferences (timezones, zoom, all-day slot state) may be
    // borrowed from another route's planner (see the `instanceRoute` prop);
    // everything else stays under this instance's own prefix.
    const calendarStoragePrefix = props.instanceRoute
      ? plannerStoragePrefix(props.instanceRoute)
      : storagePrefix;
    const dates = ref<Date[]>([]);
    const setDates = debounce((date: Date) => {
      if (isWeekView.value) {
        // Rolling N-day views display [anchor, anchor+N); their FullCalendar
        // view starts exactly at `date`. The fixed week views (5-day / 7-day)
        // render the *week containing* the anchor (timeGridWeek snaps to
        // `startOfWeek`), so the queried days must be anchored to the week
        // start too — otherwise, when `date` isn't the week start (e.g. after
        // navigating in a rolling view then switching to 5-day), the visible
        // columns and the loaded events don't line up and some days look empty.
        const start = isThreeDayView.value
          ? date
          : startOfWeek(date, {
              weekStartsOn: isWeekStartOnSunday.value ? 0 : 1,
            });
        const n = isThreeDayView.value
          ? customDays.value
          : isWeekendShown.value
            ? 7
            : 5;
        dates.value = getDates(start, n);
      } else {
        // Required
        nextTick(() => {
          const api = calendarAPI();
          if (api) {
            const start = api.view.activeStart;
            const end = addDays(api.view.activeEnd, -1);
            dates.value = eachDayOfInterval({ start, end });
          }
        });
      }
    }, 300);
    const footerEl = ref<InstanceType<typeof PlannerFooter>>();
    const unselectItems = () => {
      discardOpenEventChanges();
      // The sidebar task sections are shown in every layout (under the date
      // picker in month view); the footer only exists in week views.
      sidebarBatchEl.value?.unselectTask?.();
      if (isWeekView.value) footerEl.value?.unselectTask?.();
    };
    const updateCalendarDate = (
      date: Date,
      options?: {
        forceUpdate?: boolean;
        avoidGoingToDate?: boolean;
        // TODO: provide a method stopLoadingEvents from events.ts
      }
    ) => {
      const oldDate = cloneDeep(calendarDate.value);
      if (!hasOpenEventChanges.value)
        killEventDetails(undefined, false, "calendar-date-updated");
      if (!isWeekView.value && date.getDate() !== 1) date = startOfMonth(date);
      if (!isEqual(date, oldDate) || options?.forceUpdate) {
        // Only reassign `calendarDate` when it actually changes. Under
        // `forceUpdate` we still refresh the dates window and re-sync
        // FullCalendar below, but assigning an equal-but-fresh `Date` here would
        // needlessly wake this ref's watchers (e.g. trigger an extra `setDates`).
        if (!isEqual(date, oldDate)) calendarDate.value = date;
        setDates(date); // will load events
        const api = calendarAPI();
        if (api) {
          if (!isEqual(api.getDate(), date) && !options?.avoidGoingToDate) {
            api.gotoDate(date);
          }
        }
        if (isWeekView.value) unselectSidebarTask();
      }
    };
    const goToDate = (date: Date) => {
      const formatted = formatCalendarDate(date, isWeekStartOnSunday, {
        isThreeDayView,
        isWeekView,
      });
      pause(() =>
        updateCalendarDate(formatted, {
          forceUpdate: true,
        })
      );
    };
    const updatePlannerView = (newDate: Date) => {
      // sarting another week
      if (!isEqual(getWeek(newDate), week.value)) updateCalendarDate(newDate);
      if (isWeekView.value) {
        const newTime = newDate.getHours() - 1 + ":00";
        const api = calendarAPI();
        if (api) api.scrollToTime(newTime);
      }
    };
    onMounted(() => {
      // External callers (menu, date stamps, deep links) steer this planner
      // through query params rather than mutating shared state. Supported params:
      //   - `layout`: a layout name from `PLANNER_LAYOUTS` (e.g. "week",
      //     "month", "3-day"); ignored if it isn't a known layout.
      //   - `date`: an ISO-8601 date/datetime string (e.g. "2026-06-05"), parsed
      //     with `parseISO`; ignored if it doesn't parse.
      //   - `event`: an event id string; the matching event is opened on mount.
      applyRouteViewState();

      const date = formatCalendarDate(calendarDate.value, isWeekStartOnSunday, {
        isThreeDayView,
        isWeekView,
      });
      pause(() => updateCalendarDate(date, { forceUpdate: true }));

      // Handle event query parameter
      if (route.query.event && typeof route.query.event === "string") {
        const eventId = route.query.event;
        setTimeout(() => {
          selectEvent(eventId, true);
        }, 100);
      }
    });
    onBeforeUnmount(() => {
      unselectItems();
      killEventDetails(undefined, false, "planner-unmounted");
    });
    const week = computed<Week>(() => getWeek(calendarDate.value));
    const todayWeek = computed<Week>(() => getWeek(today.value));
    // Week of the sidebar's Week section: the displayed week in the week
    // layouts (the week of the first visible day, like the footer). The
    // month grid spans several weeks, so it keeps today's week there.
    const sidebarWeek = computed<Week>(() =>
      isWeekView.value ? week.value : todayWeek.value
    );
    const isSidebarWeekPast = computed<boolean>(
      () =>
        sidebarWeek.value.year < todayWeek.value.year ||
        (sidebarWeek.value.year === todayWeek.value.year &&
          sidebarWeek.value.n < todayWeek.value.n)
    );
    const updateCalendarSize = () => {
      // Defer to the next tick so FullCalendar measures the container *after*
      // the left pane has actually been added to / removed from the DOM.
      // Otherwise it can measure the stale (pre-update) width and leave empty
      // space — e.g. when the temporary event pane closes in month view.
      nextTick(() => {
        const api = calendarAPI();
        if (api) api.updateSize();
      });
    };
    // Effective display flags (omitted fields default to shown).
    const showZoomButtons = computed(() => props.display.zoom ?? true);
    const showLayoutSelector = computed(() => props.display.layouts ?? true);
    const showDateNavigation = computed(() => props.display.date ?? true);
    const showTimezones = computed(() => props.display.timezones ?? true);
    const isLeftPaneAvailable = computed(() => props.display.sidebar ?? true);
    const showBottomPane = computed(() => props.display.footer ?? true);
    const {
      hideLeftPane,
      isLeftPaneShown: isLeftPaneToggledOn,
      leftPaneState,
      showLeftPane,
      release: releaseLeftPane,
    } = useLeftPane(
      profile,
      `${storagePrefix}:panes/left:state`,
      updateCalendarSize
    );
    releaser.defer(releaseLeftPane);
    // The pane is only shown when it is both available (prop) and toggled on.
    const isLeftPaneShown = computed(
      () => isLeftPaneAvailable.value && isLeftPaneToggledOn.value
    );
    provide(keyUseLeftPane, {
      hideLeftPane,
      isLeftPaneShown,
      showLeftPane,
    });
    // OPEN EVENT
    const {
      confirm: confirmOpenEventChanges,
      discard: discardOpenEventChanges,
      event: openEvent,
      eventId: openEventId,
      hasUnsavedChanges: hasOpenEventChanges,
      initializeComponentFocus,
      installRouteCallbacks: installEventRouteCallbacks,
      isDraftEvent: isOpenEventDraft,
      isUpdating: isOpenEventUpdating,
      killEventDetails,
      propsEdited,
      release: releaseOpenEvent,
      selectEvent,
      setOriginalValue,
    } = useEvent(
      {
        route,
        router,
        controller,
        logger: baseLogger,
        simpleModal,
        multipleEventsModal,
      },
      { unselectItems, calendarAPI, updatePlannerView }
    );
    installEventRouteCallbacks();
    releaser.defer(releaseOpenEvent);
    onMounted(initializeComponentFocus);
    onUpdated(initializeComponentFocus);
    const defaultCalendar = computed(() => calendars.value?.default ?? null);
    const [
      openEventTask,
      originalValue,
      {
        weekTaskIds: todayWeekTaskIds,
        overdueTaskIds,
        upcomingTaskIds,
        unplannedTaskIds,
        isLoadingTasks,
      },
      {
        scheduledTaskIds,
        weekTaskIds: visibleWeekTaskIds,
        isLoadingTasks: isLoadingFooterTasks,
      },
      { selectedCalendarId, registerKeyboardListener },
    ] = await (() => {
      const openEventTask = releaser.add(
        bindAsync(openEvent, async (event) => {
          if (event?.task) return await controller.task.get(event.task);
          else return releasableRef(null);
        })
      );
      const originalValue = releaser.add(
        bindAsync(openEventId, async (id) => {
          if (id) return await controller.event.get(id);
          else return releasableRef(null);
        })
      );
      // TASKS — two batches with different week anchors:
      // - today's week feeds the sidebar's Today / Overdue / Upcoming /
      //   Unplanned sections (and its Week section in month view), so it
      //   only needs to load while the left pane is visible. The controller
      //   computes overdue and upcoming relative to the batch's week:
      //   anchored to the displayed week, they would e.g. list this week's
      //   remaining tasks as overdue while viewing next week;
      const todayTasks = releaser.add(
        useTasks(
          controller,
          todayWeek,
          today,
          isWeekStartOnSunday,
          isLeftPaneShown
        )
      );
      // - the displayed week feeds the footer's per-day lists and the
      //   sidebar's Week section (week views only).
      const visibleWeekTasks = releaser.add(
        useTasks(
          controller,
          week,
          today,
          isWeekStartOnSunday,
          computed(
            () =>
              isWeekView.value &&
              (showBottomPane.value || isLeftPaneShown.value)
          )
        )
      );
      // CALENDAR SWITCHER
      const calendarSwitcher = releaser.add(
        useCalendarSwitcher(calendars, profile)
      );
      return Promise.all([
        openEventTask,
        originalValue,
        todayTasks,
        visibleWeekTasks,
        calendarSwitcher,
      ]);
    })();
    releaser.defer(
      watch(
        calendarDate,
        (date, oldDate) => {
          // Skip the immediate run (oldDate === undefined): `onMounted` already
          // performs the initial forced navigation. Acting here too would issue
          // a second forced write to the shared `calendarDate` on every mount,
          // which—while a previous instance is briefly alive under Suspense—lets
          // the two Planners ping-pong over the singleton.
          if (
            oldDate &&
            !isEqual(date, oldDate) &&
            isEqual(getWeek(date), getWeek(today.value))
          )
            goToDate(date);
        },
        { immediate: true }
      )
    );
    setOriginalValue(originalValue);

    const sidebarWeekTaskIds = computed(() =>
      isWeekView.value ? visibleWeekTaskIds.value : todayWeekTaskIds.value
    );
    const footerDays = computed(() => {
      if (!isThreeDayView.value) return scheduledTaskIds.value;
      const weekStartDate = startOfWeek(calendarDate.value, {
        weekStartsOn: isWeekStartOnSunday.value ? 0 : 1,
      });
      return dates.value.map((date) => {
        const dayIndex = differenceInDays(
          startOfDay(date),
          startOfDay(weekStartDate)
        );
        if (dayIndex >= 0 && dayIndex < 7) {
          return scheduledTaskIds.value[dayIndex] ?? { todo: [], other: [] };
        }
        return { todo: [], other: [] };
      });
    });

    // Watch for changes to the query parameter when already on the Planner
    releaser.defer(
      watch(
        () => route.query.event,
        (eventId, oldEventId) => {
          if (
            eventId &&
            typeof eventId === "string" &&
            eventId !== oldEventId
          ) {
            setTimeout(() => {
              selectEvent(eventId, true);
            }, 100);
          }
        }
      )
    );
    // Same for date deep links (e.g. the dashboard calendar widget's open
    // button): query-only changes don't remount the route component, so the
    // onMounted handling above never re-runs.
    releaser.defer(
      watch(
        () => route.query.date,
        (dateQuery) => {
          if (typeof dateQuery !== "string") return;
          const parsed = parseISO(dateQuery);
          if (!Number.isNaN(parsed.getTime())) goToDate(parsed);
        }
      )
    );
    // Follow the controlled date (the initial value was seeded above,
    // before the date-dependent subscriptions started).
    releaser.defer(
      watch(
        () => props.date,
        (date) => {
          if (date) goToDate(date);
        }
      )
    );
    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      return {
        ...assignShortcuts(injection, goToNextPeriod, nextPeriod),
        ...assignShortcuts(injection, goToPreviousPeriod, prevPeriod),
        ...assignShortcuts(injection, goToNow, () => goToDate(today.value)),
      };
    });
    function getWeek(date: Date): Week {
      return isWeekStartOnSunday.value && getDay(date) === 0
        ? weekOfDate(addDays(date, 1))
        : weekOfDate(date);
    }
    const isCurrentPeriod = computed<boolean>(() => {
      if (isThreeDayView.value) {
        const start = startOfDay(calendarDate.value);
        const end = addDays(start, customDays.value);
        const todayStart = startOfDay(today.value);
        return todayStart >= start && todayStart < end;
      }
      if (isWeekView.value) return isEqual(getWeek(today.value), week.value);
      else return today.value.getMonth() === calendarDate.value.getMonth();
    });
    const isPastPeriod = computed<boolean>(() => {
      if (isWeekView.value)
        return isAfter(today.value, startOfDay(calendarDate.value));
      else return today.value.getMonth() < calendarDate.value.getMonth();
    });
    const isFuturePeriod = computed<boolean>(() => {
      if (isWeekView.value)
        return isAfter(startOfDay(calendarDate.value), today.value);
      else return today.value.getMonth() > calendarDate.value.getMonth();
    });
    // TASK
    const updateTask = (taskId: string, scheduled: Week | Date): void => {
      controller.task.update(
        taskId,
        {
          scheduled,
        },
        "app",
        null
      );
    };
    // DRAGGABLE
    // Receives `sidebarWeek`, not `week`: dropping a task on the "Week" list
    // must schedule it into the week the list displays (today's week in month
    // view), not the week of the month's first day.
    const {
      isDragCanceled,
      isDraggingResizing,
      isDraggingTask,
      isOutsideFooterDropZones,
      isOutsideSidebarTodayDropZone,
      isOutsideSidebarWeekDropZone,
      isOutsideSidebarUpcomingDropZone,
      isOutsideSidebarUnplannedDropZone,
      mirrorCreated,
      mirrorMove,
      options: draggableOptions,
      setDraggingTask,
      setupDropZone,
      start,
      stop,
      release: releaseDraggable,
    } = useDraggable(
      controller,
      plannerEl,
      dates,
      sidebarWeek,
      today,
      updateTask
    );
    releaser.defer(releaseDraggable);

    // Setup calendar switching keyboard listeners
    registerKeyboardListener(isDraggingTask);

    const leftPanelWidth = ref(180);
    const leftPanelDisplayedWidth = computed<number>(() =>
      isLeftPaneShown.value ? leftPanelWidth.value : 0
    );
    const onSidebarResize = (width: number) => {
      leftPanelWidth.value = width;
      updateCalendarSize();
    };
    const isFootTaskListActive = ref(false);
    const openCustomRecurrence = () => repeatModal.value.showModal(openEvent);

    releaser.defer(
      watch(openEventId, (id) => {
        if (id) showLeftPane(leftPaneState.value === "hidden");
        else if (leftPaneState.value === "hidden") hideLeftPane();
      })
    );
    const currentDay = computed<number>(() => today.value.getDate());

    // Apply a layout chosen from the selector by mapping it onto the internal
    // view-state refs; the existing watchers handle dates / FullCalendar.
    const selectLayout = (layout: PlannerLayout) => {
      const state = layoutToViewState(layout);
      // Switching into or out of month view jumps back to today (this used to
      // live in Routine.vue's global `isWeekView` watcher, now that the date is
      // per-instance it belongs here).
      if (state.isWeekView !== isWeekView.value) {
        calendarDate.value = today.value;
      }
      isWeekView.value = state.isWeekView;
      isThreeDayView.value = state.isThreeDayView;
      isWeekendShown.value = state.isWeekendShown;
      customDays.value = state.customDays;
      // All view changes — including rolling→rolling day-count changes — are
      // applied by the view-switch watcher in PlannerCalendar via `changeView`,
      // so no remount is needed (a remount caused a blink and reset scroll).
    };
    // A host can reconfigure `defaultLayout` while the planner is mounted
    // (e.g. a canvas widget's inspector): apply it like a selector pick, so
    // the change wins over the previously persisted view state. When the
    // layout selector is hidden the user has no way back to the configured
    // layout, so the prop also overrides persisted state on mount.
    releaser.defer(
      watch(
        () => props.defaultLayout,
        (layout) => {
          if (layout) selectLayout(layout);
        },
        { immediate: !showLayoutSelector.value }
      )
    );
    const updateLayoutShortcut = (index: number, layout: PlannerLayout) => {
      if (index < 0 || index >= layoutShortcuts.value.length) return;
      const updated = [...layoutShortcuts.value];
      updated[index] = layout;
      layoutShortcuts.value = updated;
    };
    // Recompute the visible date window whenever the view structure changes.
    // `setDates` branches on `isWeekView`, so this also refreshes the month
    // window (and thus its events) when switching *into* month view.
    releaser.defer(
      watch([isWeekView, isThreeDayView, isWeekendShown, customDays], () => {
        setDates(calendarDate.value);
      })
    );
    return {
      calendarDate,
      calendarStoragePrefix,
      confirmOpenEventChanges,
      currentDay,
      dates,
      defaultCalendar,
      hasCalendar,
      discardOpenEventChanges,
      draggableOptions,
      footerEl,
      hasOpenEventChanges,
      isCurrentPeriod,
      isPastPeriod,
      isFuturePeriod,
      isDragCanceled,
      isDraggingResizing,
      isDraggingTask,
      isFootTaskListActive,
      isLeftPaneAvailable,
      isLeftPaneShown,
      showBottomPane,
      showDateNavigation,
      showLayoutSelector,
      showTimezones,
      showZoomButtons,
      isLoadingTasks,
      isLoadingFooterTasks,
      isOpenEventDraft,
      isOpenEventUpdating,
      isOutsideFooterDropZones,
      isOutsideSidebarTodayDropZone,
      isOutsideSidebarWeekDropZone,
      isOutsideSidebarUpcomingDropZone,
      isOutsideSidebarUnplannedDropZone,
      isSidebarWeekPast,
      leftPaneState,
      customDays,
      isThreeDayView,
      isWeekendShown,
      isWeekView,
      killEventDetails,
      storagePrefix,
      leftPanelDisplayedWidth,
      mirrorCreated,
      mirrorMove,
      onSidebarResize,
      openCustomRecurrence,
      openEvent,
      openEventId,
      openEventTask,
      plannerCalendarEl,
      plannerEl,
      propsEdited,
      footerDays,
      selectedCalendarId,
      selectEvent,
      layoutShortcuts,
      setDraggingTask,
      setupDropZone,
      sidebarWeek,
      sidebarWeekTaskIds,
      shortcuts,
      sidebarBatchEl,
      start,
      stop,
      overdueTaskIds,
      upcomingTaskIds,
      unplannedTaskIds,
      selectLayout,
      updateLayoutShortcut,
      unselectItems,
      updateCalendarDate,
      updateCalendarSize,
      updatePlannerView,
    };
  },
});
</script>
<style scoped lang="scss">
.planner {
  display: flex;
  flex: 1;
  flex-direction: row;
  max-height: 100vh;
  overflow: hidden;

  :deep(.draggable-mirror) {
    @include themed() {
      border: 1px solid t($grey-10);
      border-radius: 3px;
      background-color: t($bg);
    }

    &.zTop {
      z-index: $z-index-modal;
    }

    &.hidden {
      opacity: 0;
    }
  }

  .days-wrapper {
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow-y: scroll;
    position: relative;
    &.isMonthView {
      margin-top: 46px;
    }
  }

  .loading {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    row-gap: 16px;

    .task {
      display: flex;
    }
  }
  .no-default-calendar {
    flex: 1;
    min-width: 0;
    min-height: 0;
    width: 100%;
    overflow: hidden;
  }
}
</style>
